import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import { AuthContext } from '../../AuthContext';
import "../../assets/CSS/Dashboard.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export default function KPIPenaltyDashboard() {
  const { user } = useContext(AuthContext);
  const [penalties, setPenalties] = useState([]);
  const [kpiEvents, setKpiEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const fetchPenalties = async () => {
    try {
      setLoading(true);
      const [res, kpiRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/wip-dashboard/admin/penalties`, { withCredentials: true }),
        axios.get(`${BASE_URL}/api/wip-dashboard/kpi-events`, { withCredentials: true })
      ]);
      if (res.data.success) {
        setPenalties(res.data.penalties);
      }
      if (kpiRes.data.success) setKpiEvents(kpiRes.data.events || []);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch penalty records', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPenalties();
  }, []);

  const handleAction = async (penalty_id, action) => {
    const isApproved = action === 'APPROVED';
    const actionText = isApproved ? 'Approve Deduction' : 'Waive / Reject';

    Swal.fire({
      title: `Confirm ${actionText}?`,
      text: isApproved ? 'This will send the deduction to payroll.' : 'This will waive the penalty for the employee.',
      icon: isApproved ? 'warning' : 'info',
      showCancelButton: true,
      confirmButtonText: actionText,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.post(`${BASE_URL}/api/wip-dashboard/admin/penalties/action`, {
            penalty_id,
            action,
            reviewed_by: user?.Name || 'SuperAdmin'
          }, { withCredentials: true });
          
          if (res.data.success) {
            Swal.fire('Success', res.data.message, 'success');
            fetchPenalties();
          }
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to update penalty', 'error');
        }
      }
    });
  };

  // Restrict access
  if (user?.Auth !== 'Admin' && user?.Auth !== 'SuperAdmin') {
    return (
      <div className="d-flex dashboard">
        <Sidebar />
        <div className="flex-grow-1">
          <Header />
          <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
            <h3 className="text-danger">Access Denied. Admins Only.</h3>
          </div>
        </div>
      </div>
    );
  }

  const pendingCount = penalties.filter(p => p.status === 'PENDING_REVIEW' || p.status === 'PENDING').length;

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-0 pb-5" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>
          {/* HEADER BANNER */}
          <div style={{ background: "linear-gradient(135deg, #475569 0%, #0f172a 100%)", padding: "40px 48px", color: "#fff", position: "relative", overflow: "hidden" }}>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
              <div style={{ background: "rgba(255, 255, 255, 0.1)", padding: "10px", borderRadius: "12px", marginRight: "16px" }}>
                <i className="bi bi-bank text-white" style={{ fontSize: "24px" }}></i>
              </div>
              KPI & Penalty Review
            </h1>
            <p style={{ margin: "12px 0 0 64px", opacity: 0.8, fontSize: 16, fontWeight: 500, maxWidth: "600px" }}>
              Review system-generated show-cause notices for permanent SLA breaches. Approve for payroll cut or waive.
            </p>
          </div>

          <div className="container-fluid mt-4" style={{ maxWidth: "1400px", padding: "0 40px" }}>
            
            {/* KPI STATS CARDS */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card border-0 p-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", background: "#fff", borderLeft: "4px solid #f59e0b" }}>
                  <h6 style={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", fontSize: "12px" }}>Pending Reviews</h6>
                  <h2 style={{ fontWeight: 800, margin: 0, color: "#0f172a" }}>{pendingCount}</h2>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 p-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", background: "#fff", borderLeft: "4px solid #ef4444" }}>
                  <h6 style={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", fontSize: "12px" }}>Critical Watchlist</h6>
                  <p style={{ margin: 0, fontSize: "14px", color: "#475569" }}>Top repeat offenders will appear here based on KPI points.</p>
                </div>
              </div>
            </div>

            {/* PENALTY TABLE */}
            <div className="card border-0" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", background: "#fff" }}>
              <div className="card-header bg-white border-bottom-0 pt-4 pb-3 px-4 d-flex justify-content-between align-items-center">
                <h5 style={{ fontWeight: 800, color: "#0f172a", margin: 0 }}>Penalty Recommendations</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
                    <thead style={{ background: "#f8fafc" }}>
                      <tr>
                        <th className="px-4 py-3 border-0 text-muted" style={{ fontWeight: 600 }}>Date</th>
                        <th className="px-4 py-3 border-0 text-muted" style={{ fontWeight: 600 }}>Incident</th>
                        <th className="px-4 py-3 border-0 text-muted" style={{ fontWeight: 600 }}>Employee</th>
                        <th className="px-4 py-3 border-0 text-muted" style={{ fontWeight: 600 }}>Proposed Penalty</th>
                        <th className="px-4 py-3 border-0 text-muted" style={{ fontWeight: 600 }}>Status</th>
                        <th className="px-4 py-3 border-0 text-muted text-end" style={{ fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan="6" className="text-center py-5">Loading...</td></tr>
                      ) : penalties.length === 0 ? (
                        <tr><td colSpan="6" className="text-center py-5 text-muted">No penalty records found.</td></tr>
                      ) : (
                        penalties.map((p, idx) => (
                          <tr key={idx} style={{ verticalAlign: "middle" }}>
                            <td className="px-4 py-3 text-nowrap">{new Date(p.created_at).toLocaleDateString('en-IN')}</td>
                            <td className="px-4 py-3">
                              <span style={{ fontWeight: 600, color: "#0f172a" }}>Lot: {p.lot_id}</span><br />
                              <span style={{ fontSize: "12px", color: "#64748b" }}>Ticket #{p.ticket_id} ({p.defect_category})</span>
                            </td>
                            <td className="px-4 py-3">
                              <span style={{ fontWeight: 600 }}>{p.worker_name || p.worker_id}</span><br/>
                              <span style={{ fontSize: "12px", color: "#64748b" }}>ID: {p.worker_id}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="badge" style={{ background: "#fee2e2", color: "#dc2626", fontWeight: 700, padding: "6px 12px", borderRadius: "8px" }}>
                                Deduct 1-Day Salary
                              </span><br/>
                              <span style={{ fontSize: "11px", color: "#94a3b8" }}>{p.reason}</span>
                            </td>
                            <td className="px-4 py-3">
                              {p.status === 'PENDING_REVIEW' || p.status === 'PENDING' ? (
                                <span className="badge bg-warning text-dark px-3 py-2 rounded-pill">Pending Review</span>
                              ) : p.status === 'APPROVED' ? (
                                <span className="badge bg-danger px-3 py-2 rounded-pill">Approved (Payroll)</span>
                              ) : (
                                <span className="badge bg-success px-3 py-2 rounded-pill">Waived</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-end">
                              {(p.status === 'PENDING_REVIEW' || p.status === 'PENDING') && (
                                <div className="d-flex gap-2 justify-content-end">
                                  <button onClick={() => handleAction(p.penalty_id, 'APPROVED')} className="btn btn-sm" style={{ background: "#dc2626", color: "#fff", fontWeight: 600 }}>
                                    Approve
                                  </button>
                                  <button onClick={() => handleAction(p.penalty_id, 'REJECTED')} className="btn btn-sm" style={{ background: "#f1f5f9", color: "#475569", fontWeight: 600 }}>
                                    Waive
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card border-0 mt-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <div className="card-header bg-white pt-4 px-4"><h5 className="fw-bold">KPI Event History</h5></div>
              <div className="table-responsive"><table className="table table-hover mb-0"><thead><tr><th>Employee</th><th>Event</th><th>Points</th><th>Lot</th><th>Date</th></tr></thead><tbody>{kpiEvents.length ? kpiEvents.map(e => <tr key={e.kpi_event_id}><td>{e.person_id}</td><td>{e.event_type}</td><td className={e.points < 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>{e.points > 0 ? '+' : ''}{e.points}</td><td>{e.lot_id || '-'}</td><td>{new Date(e.created_at).toLocaleString('en-IN')}</td></tr>) : <tr><td colSpan="5" className="text-center text-muted py-4">No KPI events yet.</td></tr>}</tbody></table></div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}
